import { ScopeButton } from '@monorepo/shared-lib';
import { FormInput } from 'components/Custom/inputs';
import { FormSelect } from 'components/Custom/selects';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ExcelTransactionAccountType } from 'store/app/enums';
import { getExcelTransactionAccountTypeOptions } from 'utils/display-enum-label';
import { createChartOfAccountsCustomRowRequest } from 'store/chart-of-accounts/actions';
import { CoaAccountRow } from 'store/chart-of-accounts/models';
import styled from 'styled-components';

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  padding: 16px 20px 24px;

  .field-label {
    font-family: Inter, sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 6px;
    display: block;
  }

  .field-group {
    margin-bottom: 16px;
  }
`;

interface AddCustomCoaAccountProps {
  organisationId: string;
}

export const AddCustomCoaAccount: React.FC<AddCustomCoaAccountProps> = ({
  organisationId,
}) => {
  const { t } = useTranslation();
  const accountTypeOptions = React.useMemo(
    () => getExcelTransactionAccountTypeOptions(t),
    [t]
  );
  const dispatch = useDispatch();
  const [name, setName] = React.useState('');
  const [accountType, setAccountType] = React.useState<string | undefined>(
    undefined
  );

  const canSave = organisationId && name.trim() && accountType;

  const handleSave = () => {
    if (!canSave || !accountType) return;

    const row: CoaAccountRow = {
      id: '', // backend will generate id
      name: name.trim(),
      type: accountType as ExcelTransactionAccountType, // send enum key (capital value)
      recommended: true,
      custom: true,
    };

    dispatch(createChartOfAccountsCustomRowRequest(organisationId, row));
  };

  return (
    <Wrapper>
      <div className="field-group">
        <label htmlFor="custom-coa-name" className="field-label">
          {t('organisation.account-name')}
        </label>
        <FormInput
          id="custom-coa-name"
          width="100%"
          placeholder={t('organisation.enter-account-name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field-group">
        <span className="field-label">{t('organisation.account-type')}</span>
        <FormSelect
          placeholder={t('organisation.select-account-type')}
          value={accountType}
          onChange={(val) => setAccountType(val as string)}
          width="100%"
          height="45px"
          bordercolor="var(--color-border, #dbeae3)"
          bgcolor="var(--color-surface-raised, #FFFFFF)"
        >
          {accountTypeOptions.map((opt) => (
            <FormSelect.Option key={opt.value} value={opt.value}>
              {opt.label}
            </FormSelect.Option>
          ))}
        </FormSelect>
      </div>

      <ScopeButton
        type="primary"
        width="100%"
        height="40px"
        fontSize="14px"
        onClick={handleSave}
        disabled={!canSave}
      >
        {t('organisation.add-account')}
      </ScopeButton>
    </Wrapper>
  );
};
